import * as React from "react";
import * as ReactDOM from "react-dom";
import * as models from "../../models";
import * as modelsGet from "../../models/get";
import { TableRow, TextColumn, ITableColumn, ITableRowEventArgs } from "./";
import { VirtualList } from "../virtualList";

interface ITableProperties
{
    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Die gebundenen Daten.
     */
    dataSource: any[],

    /**
     * Die spalten Konfiguration.
     */
    columns: ITableColumn[],

    /**
     * Name einer Eindeutigen Eigenschaft in der DataSource.
     */
    keyAccessor?: string,

    /**
     * Der Index der selekierten Zeile.
     */
    selectedRowIndex?: string,

    /**
     * Gibt an, ob die Tabelle abgeschaltet ist.
     */
    isDisabled?: boolean,

    /**
     * Wird ausgeführt, sobald auf eine Zeile geklickt wird.
     */
    onRowClick?: (e: ITableRowEventArgs<any>) => void,

    /**
     * Wird ausgeführt, sobald auf eine Zeile doppel geklickt wird.
     */
    onRowDoubleClick?: (e: ITableRowEventArgs<any>) => void,

    /**
     * Wird ausgeführt, sobald auf eine Zeile mit der rechten Maustaste geklickt wird.
     */
    onRowRightClick?: (e: ITableRowEventArgs<any>) => void
}

interface ITableState
{
    /**
     * Enthält die momentan selektierte Zeile.
     */
    selectedRowIndex: string
}

export class Table extends React.Component<ITableProperties, ITableState>
{
    private _headerRef: HTMLDivElement;

    public static defaultProps: Partial<ITableProperties> = {
        dataSource: [],
        columns: []
    };

    constructor(props: ITableProperties)
    {
        super(props);

        this.state = {
            selectedRowIndex: props.selectedRowIndex
        };
    }

    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.emitEvent("onRowClick", e);
    }

    private onRowDoubleClick = (e: ITableRowEventArgs<any>) =>
    {
        this.emitEvent("onRowDoubleClick", e);
    }

    private onRowRightClick = (e: ITableRowEventArgs<any>) =>
    {
        this.emitEvent("onRowRightClick", e);
    }

    private onScroll = (e) =>
    {
        if (this._headerRef)
        {
            var scrollLeft: number = e.target != null ? e.target.scrollLeft : e;
            this._headerRef.scrollLeft = scrollLeft;
        }
    }

    /**
     * Führt ein Ereignis aus wenn dieses vom übergeordneten Element angegeben wurde.
     * @param e Das auslösende Ereignis.
     * @param event Der Name des Ereignis welches ausgeführt werden soll.
     */
    private emitEvent(event: string, e: ITableRowEventArgs<any>)
    {
        this.setState({
            selectedRowIndex: e.index
        });

        if (this.props[event] != null)
            this.props[event](e);
    }

    render()
    {
        const { style, columns, dataSource, keyAccessor, isDisabled } = this.props;
        const { selectedRowIndex } = this.state;
        const tableClassNames: string[] = ["n-table"];

        if (isDisabled)
            tableClassNames.push("n-table--disabled")

        return (
            <div
                style={style}
                className="n-table-container">
                <div
                    ref={(ref) => this._headerRef = ref}
                    className="n-table-header">
                    <TableRow isHeader={true}>
                        {
                            columns.map((column) =>
                            {
                                var width: number = (column.width != null) ? column.width : 180;
                                return (
                                    <TextColumn
                                        key={column.header}
                                        text={column.header}
                                        width={width} />
                                );
                            })
                        }
                    </TableRow>
                </div>
                <div className={tableClassNames.join(" ")}>
                    <VirtualList
                        width="100%"
                        height="100%"
                        itemCount={dataSource.length}
                        itemHeight={30}
                        onScroll={this.onScroll}>
                        {
                            (index, style) =>
                            {
                                let data = dataSource[index];
                                let key: string = (keyAccessor != null) ? data[keyAccessor] : index;
                                let isSelected: boolean = selectedRowIndex === key.toString();

                                return (
                                    <TableRow
                                        style={style}
                                        key={key.toString()}
                                        rowIndex={key.toString()}
                                        dataSource={data}
                                        isSelected={isSelected}
                                        onClick={this.onRowClick}
                                        onDoubleClick={this.onRowDoubleClick}
                                        onRightClick={this.onRowRightClick}>
                                        {
                                            columns.map((column, index) =>
                                            {
                                                let cell: JSX.Element = (column.cell != null) ? column.cell(data) : null;

                                                if (cell != null)
                                                {
                                                    return cell;
                                                }
                                                else
                                                {
                                                    let value: string = (column.computedValue != null) ? column.computedValue(data) : data[column.accessor];
                                                    let width: number = (column.width != null) ? column.width : 180;
                                                    return (
                                                        <TextColumn
                                                            key={index}
                                                            text={value}
                                                            width={width} />
                                                    );
                                                }
                                            })
                                        }
                                    </TableRow>
                                );
                            }
                        }
                    </VirtualList>
                </div>
            </div>
        );
    }
}